import { SummaryExportButton } from '@/components/shared-ui/summary-export';
import { excelDateToJSDate } from '@listening/utils/ts-utils';
import { formatDate } from '@/server/lib/utils';
import {
  DocumentSummarySection,
  DocumentDetailsSection,
  DocumentKeyPointsSection,
} from '@/components/shared-ui/document-insights';
import { documentKeyPoints } from '@listening/frontend/query/document-insights';
import { clientEnv } from '@/env';
import { DocumentInsightsSidebar } from './document-insights-sidebar';
import {
  ReadingLabelsPanel,
  ReadingLabelsResetButton,
} from './reading-labels-settings';
import {
  primaryButtonSurface,
  secondaryButtonSurface,
} from '@/components/web-fe/design-v2/surfaces';
import { apiFailureMessage } from '@listening/frontend/query/query-error';
import { getNotePlaybackTarget } from '@listening/frontend/audio-chunk-player/note-playback';
import {
  activeDocPackOutlineNavigationItem,
  docPackOutlineNavigationItems,
} from '@listening/frontend/doc-pack/navigation';
import type { NoteRow as Note } from '@listening/frontend/query/notes';
import { cn } from '@listening/utils/react-helpers';
import AddIcon from '@listening/assets/icons/add.svg?react';
import DeleteIcon from '@listening/assets/icons/delete.svg?react';
import DownloadIcon from '@listening/assets/icons/download.svg?react';
import NextIcon from '@listening/assets/icons/next.svg?react';
import PencilIcon from '@listening/assets/icons/pencil.svg?react';
import ShareIcon from '@listening/assets/icons/share2.svg?react';
import VolumeIcon from '@listening/assets/icons/volume.svg?react';

import { Button } from '@listening/frontend/components/radix/button';
import {
  Dialog,
  DialogTrigger,
} from '@listening/frontend/components/radix/dialog';
import { DialogCloseProvider } from '@listening/frontend/components/custom/dialog-dropdown';
import { ScrollArea } from '@listening/frontend/components/radix/scroll-area';
import { TooltipButton } from '@listening/frontend/components/custom/tooltip-button';
import { Play, Xmark } from 'iconoir-react';
import type React from 'react';
import { toast } from 'sonner';
import * as m from '@/paraglide/messages';
import { ErrorComponent } from '@/components/shared-ui/error-component';
import { LoadingComponent } from '@/components/shared-ui/loading-component';
import { Spinner } from '@/components/shared-ui/spinner';
import { EditNoteDialog } from '@/components/web-fe/dialogs/edit-note-dialog';
import { ExportNotesDialog } from '@/components/web-fe/dialogs/export-notes-dialog';
import ShareNotesDialog from '@/components/web-fe/dialogs/share-notes-dialog';
import EmptyNotes from '@/components/web-fe/notes/empty-notes';
import {
  useAudioChunkPlayer,
  useAudioChunkSelector,
  useDocPack,
  useDocumentSummary,
} from '@/lib/audio-chunk-player';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { audioService } from '@/services/audio-service';
import {
  useAddAudioNote,
  useDeleteNoteMutation,
} from '@/services/notes-service';
import { audioNotesAtom } from '@listening/frontend/query/notes';
import { idleQuery } from '@listening/frontend/query/api-runtime';
import { useAtomValue } from '@effect/atom-react';
import { Exit } from 'effect';
import { AsyncResult } from 'effect/reactivity';
import {
  type SidebarState,
  useReadAlongContext,
  useReadAlongPageType,
  useReadAlongSidebarStore,
} from './read-along-store';

const contentMap: Record<SidebarState, React.ReactNode> = {
  notes: <NotesSideBar />,
  information: <ReadAlongMetadataSidebar />,
  sections: <ChaptersSideBar />,
  reading: <ReadingLabelsPanel />,
  closed: null,
};

export function ReadAlongSideBar({ className }: { className?: string }) {
  const { setSidebarState, sidebarState } = useReadAlongSidebarStore();
  const { isWebAppDoc } = useReadAlongPageType();
  const showInsights = isWebAppDoc && clientEnv.documentInsightsEnabled;
  const isClosed =
    sidebarState === 'closed' ||
    (sidebarState === 'information' && showInsights);
  const titleMap: Record<SidebarState, string> = {
    notes: m.common_notes(),
    information: m.read_along_information(),
    sections: m.playbar_sections(),
    reading: m.reading_labels_title(),
    closed: '',
  };

  return (
    <>
      {showInsights && <DocumentInsightsSidebar className={className} />}
      <section
        aria-hidden={isClosed}
        inert={isClosed}
        className={cn(
          'absolute inset-x-0 bottom-0 z-5 flex h-[min(70%,36rem)] flex-col overflow-hidden rounded-t-[20px] bg-[var(--v2-page-bg)] shadow-[0_-16px_40px_-16px_rgb(0_0_0/0.25)] transition-[width,visibility] duration-350 ease-out motion-reduce:transition-none md:relative md:inset-auto md:h-auto md:shrink-0 md:rounded-none md:shadow-none',
          isClosed
            ? 'invisible md:w-0'
            : 'visible border-t border-t-[var(--v2-sidebar-border)] md:w-[20rem] md:border-t-0 md:border-l md:border-l-[var(--v2-sidebar-border)] lg:w-[22.75rem]',
          className,
        )}
      >
        <div
          className={cn(
            'flex h-full min-h-0 w-full flex-col pt-2 transition-[opacity,transform] duration-350 ease-out motion-reduce:transition-none md:w-[20rem] md:min-w-[20rem] md:pt-6 lg:w-[22.75rem] lg:min-w-[22.75rem]',
            isClosed
              ? 'translate-x-4 opacity-0 max-md:translate-x-0 max-md:translate-y-6'
              : 'translate-x-0 translate-y-0 opacity-100',
          )}
        >
          <div
            aria-hidden
            className='mx-auto mb-2 h-1 w-9 shrink-0 rounded-full bg-[var(--v2-sidebar-border)] md:hidden'
          />
          {!isClosed && (
            <div className='flex items-center justify-between gap-2 px-5 pb-2 md:px-8'>
              <h3 className='text-lg leading-[1.875rem] font-[700] md:text-xl'>
                {titleMap[sidebarState]}
              </h3>

              <div className='flex items-center gap-1'>
                {sidebarState === 'reading' && <ReadingLabelsResetButton />}
                <TooltipButton
                  className='p-1'
                  onClick={() => {
                    setSidebarState('closed');
                  }}
                  size='sm'
                  toolTipContent={m.common_close()}
                  variant='ghost'
                >
                  <Xmark className='size-4' />
                </TooltipButton>
              </div>
            </div>
          )}
          {!isClosed && contentMap[sidebarState]}
        </div>
      </section>
    </>
  );
}

function ChaptersSideBar() {
  const { documentSummary } = useDocumentSummary();
  const audioChunkPlayer = useAudioChunkPlayer();
  const chapterId = useAudioChunkSelector((s) => s.chapterId);
  const playbackMode = useAudioChunkSelector((s) => s.playbackMode);
  const docPackChunk = useAudioChunkSelector((s) => s.docPackChunk);
  const { capture } = useAnalytics();
  const docPack = useDocPack(documentSummary).pack;
  if (!documentSummary) return <p>{m.read_along_no_audio_playing()}</p>;

  const docPackOutlineItems = docPack
    ? docPackOutlineNavigationItems(docPack.catalog)
    : [];
  const firstDocPackRange = docPackChunk?.ranges[0];
  const activeDocPackOutline = activeDocPackOutlineNavigationItem(
    docPackOutlineItems,
    firstDocPackRange
      ? { page: firstDocPackRange[0], charStart: firstDocPackRange[1] }
      : undefined,
  );
  const docPackOutlineListItems = docPackOutlineItems.map((item) => {
    const active =
      playbackMode === 'doc-pack' && activeDocPackOutline?.id === item.id;
    return (
      <Button
        className='flex h-auto w-full justify-between gap-2 p-4 text-left'
        key={item.id}
        onClick={() => {
          audioChunkPlayer.skipToDocPackRange({
            page: item.page,
            charStart: item.charStart,
          });
          capture('skip_to_chapter', {
            chapter_name: item.title,
          });
        }}
        style={{ paddingLeft: `${1 + item.depth * 0.75}rem` }}
        variant='outline'
      >
        <p className='font-bold text-wrap'>{item.title}</p>
        <div className='flex items-center gap-2'>
          {active ? (
            <VolumeIcon className='ml-auto text-accent-foreground' />
          ) : (
            <NextIcon className='ml-auto text-secondary-foreground' />
          )}
        </div>
      </Button>
    );
  });
  const chapterListItems = documentSummary.chapters.map((chapter) => (
    <Button
      className='flex h-auto w-full justify-between gap-2 p-4 text-left'
      key={chapter.id}
      onClick={() => {
        audioChunkPlayer.skipToChapterStart(chapter.id);
        capture('skip_to_chapter', {
          chapter_name: chapter.title,
        });
      }}
      variant='outline'
    >
      <p className='font-bold text-wrap'>{chapter.title}</p>
      <div className='flex items-center gap-2'>
        <p className='text-right text-azure-210' />
        {chapterId === chapter.id ? (
          <VolumeIcon className='ml-auto text-accent-foreground' />
        ) : (
          <NextIcon className='ml-auto text-secondary-foreground' />
        )}
      </div>
    </Button>
  ));
  const sectionItems =
    docPackOutlineListItems.length > 0
      ? docPackOutlineListItems
      : chapterListItems;
  return (
    <ScrollArea className='min-h-0 flex-1'>
      <div className='space-y-4 px-8 py-2 pb-32'>{sectionItems}</div>
    </ScrollArea>
  );
}

function ReadAlongMetadataSidebar() {
  const metadata = useReadAlongContext((s) => s.metadata);
  const { documentSummary } = useDocumentSummary();
  const summary = metadata?.abstract || documentSummary?.summary;
  const points = documentKeyPoints(documentSummary?.key_points);
  const authors = metadata?.authors || documentSummary?.publishing_author;
  const category = metadata?.category || documentSummary?.category;

  return (
    <ScrollArea className='min-h-0 flex-1'>
      <div className='flex flex-col gap-7 px-8 py-4 pb-32'>
        <h2 className='text-lg font-semibold text-foreground'>
          {metadata?.title || documentSummary?.title}
        </h2>
        {summary && (
          <DocumentSummarySection
            summary={summary}
            action={
              <SummaryExportButton
                title={
                  metadata?.title ||
                  documentSummary?.title ||
                  m.common_summary()
                }
                summary={summary}
                keyPoints={points}
              />
            }
          />
        )}
        {points.length > 0 && <DocumentKeyPointsSection points={points} />}
        {!summary &&
          points.length === 0 &&
          !authors &&
          !category &&
          !metadata?.source &&
          !metadata?.publication_date && (
            <p className='text-sm text-muted-foreground'>
              {m.read_along_no_metadata()}
            </p>
          )}
        {(authors || category || metadata?.source) && (
          <DocumentDetailsSection
            authors={authors}
            category={category}
            source={metadata?.source}
          />
        )}
        {metadata?.publication_date && (
          <div className='text-sm'>
            <span className='font-medium'>
              {m.read_along_publication_date()}:{' '}
            </span>
            {!Number.isNaN(Number.parseInt(metadata.publication_date, 10))
              ? formatDate(
                  excelDateToJSDate(
                    Number.parseInt(metadata.publication_date, 10),
                  ),
                )
              : m.read_along_not_available()}
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

function NotesSideBar() {
  const audioItem = audioService.store.useCurrentAudio();
  const notesResult = useAtomValue(
    audioItem ? audioNotesAtom(audioItem.id) : idleQuery,
  );
  const { addAudioNote, isAddingNote } = useAddAudioNote();

  if (!audioItem) return <p>{m.read_along_start_playing_for_notes()}</p>;
  if (AsyncResult.isInitial(notesResult)) return <LoadingComponent />;
  if (AsyncResult.isFailure(notesResult))
    return (
      <ErrorComponent
        header={
          apiFailureMessage(notesResult.cause) ??
          m.read_along_failed_to_load_notes()
        }
      />
    );

  const notes = notesResult.value;

  return (
    <>
      <div className='mt-1 mb-6 flex flex-row items-center gap-2 px-8'>
        <Button
          className={cn(
            'flex h-9 items-center gap-1.5 px-3.5 text-[13px] font-semibold',
            primaryButtonSurface,
          )}
          onClick={addAudioNote}
        >
          {isAddingNote ? <Spinner /> : <AddIcon className='h-3.5 w-3.5' />}
          {m.read_along_add_new_note()}
        </Button>
        <Dialog>
          <DialogTrigger asChild>
            <Button
              className={cn(
                'flex h-9 items-center gap-1.5 px-3.5 text-[13px] font-semibold',
                secondaryButtonSurface,
              )}
            >
              <DownloadIcon className='h-3.5 w-3.5' />
              {m.read_along_export_all_notes()}
            </Button>
          </DialogTrigger>
          <ExportNotesDialog notes={notes} />
        </Dialog>
      </div>
      {notes.length === 0 && <EmptyNotes />}
      <ScrollArea className='min-h-0 flex-1'>
        <div className='space-y-4 py-2'>
          {notes.map((note) => (
            <NotesSidebarListItem
              key={note.id}
              note={note}
              documentTitle={audioItem.title ?? undefined}
            />
          ))}
        </div>
        {/* Notes auto-load via the collection's cursor-follow queryFn — the
            "Load more" button became unreachable when notes migrated. */}
      </ScrollArea>
    </>
  );
}

const ICON_BUTTON =
  'flex size-8 items-center justify-center rounded-[var(--v2-button-radius)] p-0 text-[var(--v2-text-muted)] transition-colors hover:bg-black/[0.05] hover:text-[var(--v2-text-primary)] disabled:pointer-events-none disabled:opacity-40 dark:hover:bg-white/10';

function NotesSidebarListItem({
  note,
  documentTitle,
}: {
  note: Note;
  documentTitle?: string | undefined;
}) {
  const deleteNoteMutation = useDeleteNoteMutation(note.id);
  const audioChunkPlayer = useAudioChunkPlayer();
  const playbackTarget = getNotePlaybackTarget(note);
  // Captured notes default their title to the document's, which the panel
  // header already shows — but a title edited in `EditNoteDialog` is the
  // user's own and has to stay visible.
  const hasCustomTitle = note.title !== '' && note.title !== documentTitle;

  return (
    <div className='mx-8 flex flex-col gap-2 overflow-clip rounded-[var(--v2-card-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] bg-[var(--v2-page-bg)]'>
      <div className='flex flex-col gap-1 p-[var(--v2-gap-4)] pb-0'>
        {hasCustomTitle && (
          <p
            className='text-[13px] text-[var(--v2-text-primary)]'
            style={{ fontWeight: 'var(--v2-heading-weight)' }}
          >
            {note.title}
          </p>
        )}
        <p className='text-[14px] leading-relaxed whitespace-pre-wrap text-[var(--v2-text-primary)]'>
          {note.body}
        </p>
      </div>
      <div className='flex items-center gap-0.5 px-[var(--v2-gap-2)] pb-[var(--v2-gap-2)]'>
        <TooltipButton
          className={ICON_BUTTON}
          disabled={!playbackTarget}
          onClick={() => {
            if (!playbackTarget) return;
            audioChunkPlayer.skipToNote(playbackTarget);
          }}
          toolTipContent={m.common_play()}
          variant='ghost'
        >
          <Play className='size-3.5 shrink-0 fill-current' strokeWidth={0} />
        </TooltipButton>
        <DialogCloseProvider>
          <DialogTrigger asChild>
            <TooltipButton
              className={ICON_BUTTON}
              toolTipContent={m.common_edit()}
              variant='ghost'
            >
              <PencilIcon className='size-4' />
            </TooltipButton>
          </DialogTrigger>
          <EditNoteDialog noteItem={note} />
        </DialogCloseProvider>
        <DialogCloseProvider>
          <DialogTrigger asChild>
            <TooltipButton
              className={ICON_BUTTON}
              toolTipContent={m.common_share()}
              variant='ghost'
            >
              <ShareIcon className='size-4' />
            </TooltipButton>
          </DialogTrigger>
          <ShareNotesDialog note={note} />
        </DialogCloseProvider>
        <DialogCloseProvider>
          <DialogTrigger asChild>
            <TooltipButton
              className={ICON_BUTTON}
              toolTipContent={m.dialog_export()}
              variant='ghost'
            >
              <DownloadIcon className='size-4' />
            </TooltipButton>
          </DialogTrigger>
          <ExportNotesDialog notes={[note]} />
        </DialogCloseProvider>

        <TooltipButton
          className={cn(
            'ml-auto',
            ICON_BUTTON,
            'hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400',
          )}
          onClick={() => {
            void deleteNoteMutation.deleteNote().then((exit) => {
              if (Exit.isFailure(exit))
                toast.error(
                  apiFailureMessage(exit.cause) ??
                    m.read_along_error_deleting_note(),
                );
            });
          }}
          toolTipContent={m.common_delete()}
          variant='ghost'
        >
          {deleteNoteMutation.isPending ? (
            <Spinner />
          ) : (
            <DeleteIcon className='h-4 w-4 text-red-500 transition-colors duration-200 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300' />
          )}
        </TooltipButton>
      </div>
    </div>
  );
}
